import { Fragment, memo, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Link,
  TableSortLabel,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { grey, blue } from "@mui/material/colors";

interface SearchResult {
  result_number: number;
  content: string;
  metadata: Record<string, any>;
  data_chunk_id: number;
  source_data_record_id: number;
  source_file_name: string;
  source_file_path?: string;
  dataset_id: number;
  updated_date?: string;
}

interface SearchResultsDisplayProps {
  query: string;
  title: string;
  results: SearchResult[];
  totalResults: number;
}

type SortField = "result_number" | "source_file_name" | "dataset_id";
type SortDirection = "asc" | "desc";

const SearchResultsDisplay = memo(
  ({ query, title, results, totalResults }: SearchResultsDisplayProps) => {
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [sortField, setSortField] = useState<SortField>("result_number");
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

    const toggleRow = useCallback((resultNumber: number) => {
      setExpandedRows((prev) => {
        const next = new Set(prev);
        if (next.has(resultNumber)) {
          next.delete(resultNumber);
        } else {
          next.add(resultNumber);
        }
        return next;
      });
    }, []);

    const handleSort = useCallback(
      (field: SortField) => {
        if (sortField === field) {
          setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
          setSortField(field);
          setSortDirection("asc");
        }
      },
      [sortField],
    );

    const sortedResults = useMemo(() => {
      return [...results].sort((a, b) => {
        let cmp = 0;
        switch (sortField) {
          case "result_number":
            cmp = (a.result_number || 0) - (b.result_number || 0);
            break;
          case "source_file_name":
            cmp = (a.source_file_name || "").localeCompare(
              b.source_file_name || "",
            );
            break;
          case "dataset_id":
            cmp = (a.dataset_id || 0) - (b.dataset_id || 0);
            break;
        }
        return sortDirection === "asc" ? cmp : -cmp;
      });
    }, [results, sortField, sortDirection]);

    const handleOpenDocument = useCallback((result: SearchResult) => {
      const datasetId = Math.abs(result.dataset_id);
      const isDocument = Boolean(result.source_file_name);
      const url = isDocument
        ? `/datasets/${datasetId}/document/${encodeURIComponent(result.source_data_record_id)}`
        : `/datasets/${datasetId}/predictions/${encodeURIComponent(result.source_data_record_id)}`;
      window.open(url, "_blank");
    }, []);

    const handleExportCSV = useCallback(() => {
      const headers = [
        "#",
        "Source File",
        "Dataset ID",
        "Content",
        "Document Title",
        "Chunk ID",
        "SDR ID",
      ];
      const rows = results.map((r) => [
        r.result_number,
        r.source_file_name || "",
        r.dataset_id,
        `"${(r.content || "").replace(/"/g, '""')}"`,
        r.metadata?.document_title || "",
        r.data_chunk_id,
        r.source_data_record_id,
      ]);

      const csv = [headers.join(","), ...rows.map((r) => r.join(","))].join(
        "\n",
      );
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `search_results_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }, [results]);

    if (!results || results.length === 0) {
      return (
        <Box
          sx={{
            mt: 0,
            mb: 1,
            px: 1.5,
            py: 1.25,
            borderRadius: "0 0 6px 6px",
            bgcolor: grey[100],
            border: `1px solid ${grey[300]}`,
            borderTop: "none",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No results found for "{query}"
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          mt: 0,
          mb: 1,
          borderRadius: "0 0 6px 6px",
          border: `1px solid ${grey[300]}`,
          borderTop: "none",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: grey[50],
            borderTop: `1px solid ${grey[300]}`,
            borderBottom: `1px solid ${grey[200]}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SearchIcon sx={{ fontSize: 18, color: grey[600] }} />
            <Typography variant="subtitle2" fontWeight={600}>
              {title}
            </Typography>
            <Chip
              label={`${totalResults} result${totalResults !== 1 ? "s" : ""}`}
              size="small"
              variant="outlined"
              sx={{ height: 22 }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
              Query: "{query}"
            </Typography>
            <Tooltip title="Export to CSV">
              <IconButton size="small" onClick={handleExportCSV}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Results Table */}
        <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40, py: 0.75 }} />
                <TableCell sx={{ width: 50, py: 0.75 }}>
                  <TableSortLabel
                    active={sortField === "result_number"}
                    direction={
                      sortField === "result_number" ? sortDirection : "asc"
                    }
                    onClick={() => handleSort("result_number")}
                  >
                    #
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 0.75 }}>
                  <TableSortLabel
                    active={sortField === "source_file_name"}
                    direction={
                      sortField === "source_file_name" ? sortDirection : "asc"
                    }
                    onClick={() => handleSort("source_file_name")}
                  >
                    Source
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ py: 0.75 }}>Content Preview</TableCell>
                <TableCell sx={{ width: 60, py: 0.75 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedResults.map((result) => {
                const isExpanded = expandedRows.has(result.result_number);
                const docTitle =
                  result.metadata?.document_title || result.source_file_name;
                return (
                  <Fragment key={result.result_number}>
                    <TableRow
                      hover
                      sx={{
                        cursor: "pointer",
                        "&:hover": { bgcolor: blue[50] },
                      }}
                      onClick={() => toggleRow(result.result_number)}
                    >
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <IconButton size="small">
                          {isExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography variant="caption" fontWeight={600}>
                          {result.result_number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            maxWidth: 250,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {docTitle}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Dataset {result.dataset_id}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            color: grey[700],
                          }}
                        >
                          {result.content}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Tooltip title="Open document">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDocument(result);
                            }}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>

                    {/* Expanded detail row */}
                    <TableRow key={`${result.result_number}-detail`}>
                      <TableCell
                        colSpan={5}
                        sx={{ py: 0, px: 0, borderBottom: 0 }}
                      >
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            sx={{
                              px: 3,
                              py: 2,
                              bgcolor: grey[50],
                              borderBottom: `1px solid ${grey[200]}`,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ mb: 1.5, whiteSpace: "pre-wrap" }}
                            >
                              {result.content}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              {result.metadata?.document_type &&
                                result.metadata.document_type !== "Unknown" && (
                                  <Chip
                                    label={result.metadata.document_type}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                )}
                              {result.metadata?.document_author &&
                                result.metadata.document_author !==
                                  "Unknown" && (
                                  <Chip
                                    label={`By: ${result.metadata.document_author}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                )}
                              <Chip
                                label={`Chunk #${result.data_chunk_id}`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: "0.7rem" }}
                              />
                              <Chip
                                label={`SDR #${result.source_data_record_id}`}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: "0.7rem" }}
                              />
                            </Box>
                            <Box sx={{ mt: 1 }}>
                              <Link
                                component="button"
                                variant="caption"
                                onClick={() => handleOpenDocument(result)}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                Open in Dataset Viewer{" "}
                                <OpenInNewIcon sx={{ fontSize: 12 }} />
                              </Link>
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  },
);

SearchResultsDisplay.displayName = "SearchResultsDisplay";

export default SearchResultsDisplay;
